// Withings meastypes: 1 weight (kg), 6 fat ratio (%), 8 fat mass (kg), 76 muscle mass (kg).
// Raw readings carry a power-of-ten `unit`; real value = value * 10^unit.
import type { MeasureReading } from '@/services/PatientService/endpoints';

export const WEIGHT_TYPES = {
  weight: 1,
  fatRatio: 6,
  fatMass: 8,
  muscleMass: 76,
} as const;

export const WEIGHT_MEASTYPES = Object.values(WEIGHT_TYPES).join(',');

export interface SeriesPoint { epoch: number; value: number; }
export type WeightSeries = Record<number, SeriesPoint[]>;

export interface WeightSummary {
  count: number;
  firstWeight: number;
  lastWeight: number;
  weightDelta: number | null;
  latestFatPct: number | null;
  latestMusclePct: number | null;
}

function realValue(r: MeasureReading): number {
  return r.value * (10 ** (r.unit ?? 0));
}

// Per-type, one day-averaged point per day, sorted by epoch.
export function buildWeightSeries(rawReadings: MeasureReading[] | null | undefined): WeightSeries {
  const byType: Record<number, Map<string, { epoch: number; sum: number; n: number }>> = {};
  (rawReadings || []).forEach((r) => {
    const dayKey = new Date(r.epoch * 1000).toLocaleDateString('en-CA');
    if (!byType[r.type]) byType[r.type] = new Map();
    const dayMap = byType[r.type]!;
    if (!dayMap.has(dayKey)) dayMap.set(dayKey, { epoch: r.epoch, sum: 0, n: 0 });
    const slot = dayMap.get(dayKey)!;
    slot.sum += realValue(r);
    slot.n += 1;
    slot.epoch = r.epoch;
  });

  const series: WeightSeries = {};
  Object.entries(byType).forEach(([type, dayMap]) => {
    series[Number(type)] = Array.from(dayMap.values())
      .map((s) => ({ epoch: s.epoch, value: s.sum / s.n }))
      .sort((a, b) => a.epoch - b.epoch);
  });
  return series;
}

// Returns null when there is no weight reading.
export function computeWeightSummary(rawReadings: MeasureReading[] | null | undefined): WeightSummary | null {
  const series = buildWeightSeries(rawReadings);
  const weight = series[WEIGHT_TYPES.weight];
  if (!weight || weight.length === 0) return null;

  const latest = (type: number): number | null => {
    const s = series[type];
    return s && s.length ? s[s.length - 1]!.value : null;
  };

  const firstWeight = weight[0]!.value;
  const lastWeight = weight[weight.length - 1]!.value;

  // Mass (kg) → % of the SAME-DAY weight (the scale reports them together).
  // No same-day weight = anomalous data → null (card shows "—"), never a cross-day ratio.
  const dayKey = (epoch: number) => new Date(epoch * 1000).toLocaleDateString('en-CA');
  const weightByDay = new Map(weight.map((p) => [dayKey(p.epoch), p.value]));
  const pctOf = (type: number): number | null => {
    const s = series[type];
    if (!s || s.length === 0) return null;
    const pt = s[s.length - 1]!;
    const w = weightByDay.get(dayKey(pt.epoch));
    return w != null && w > 0 ? (pt.value / w) * 100 : null;
  };

  return {
    count: weight.length,
    firstWeight,
    lastWeight,
    weightDelta: weight.length >= 2 ? lastWeight - firstWeight : null,
    // Device-measured fat ratio wins; computed from fat mass when absent.
    latestFatPct: latest(WEIGHT_TYPES.fatRatio) ?? pctOf(WEIGHT_TYPES.fatMass),
    latestMusclePct: pctOf(WEIGHT_TYPES.muscleMass),
  };
}
